import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axiosInstance from '../components/axiosInstance';
import { LEAD_PHASES, LEAD_LOST } from '../constants/leadPhases';
import { ACCOUNT_PHASES, ACCOUNT_LOST } from '../constants/accountPhases';
import { PROJECT_PHASES, PROJECT_LOST } from '../constants/projectPhases';
import { LOST_REASONS, STALE_HINT_DAYS, STALE_WARN_DAYS } from '../constants/activity';

/**
 * Pipeline-Konfiguration (B3): Phasen + Checklisten je Objekttyp,
 * Verlustgründe und Inaktivitäts-Schwellen kommen aus dem Backend
 * (GET /pipeline-config/, im Django-Admin gepflegt) statt fest aus dem Code.
 *
 * Die gebündelten Konstanten bleiben als Fallback: erste Renderzyklen vor
 * der Antwort und der (seltene) Fehlerfall zeigen damit weiterhin eine
 * funktionierende Pipeline.
 *
 * Verwendung:
 *   const { phasesFor, lostFor, statusLabelsFor, lostReasons,
 *           staleHintDays, staleWarnDays } = usePipelineConfig();
 *   phasesFor('lead') → [{ key, label, next, checklist, staleHintDays, staleWarnDays }]
 */

const FALLBACK = {
  objects: {
    lead: { phases: LEAD_PHASES, lost: LEAD_LOST },
    account: { phases: ACCOUNT_PHASES, lost: ACCOUNT_LOST },
    project: { phases: PROJECT_PHASES, lost: PROJECT_LOST },
  },
  lostReasons: LOST_REASONS,
  staleHintDays: STALE_HINT_DAYS,
  staleWarnDays: STALE_WARN_DAYS,
};

const PipelineConfigContext = createContext(FALLBACK);

// API-Antwort (snake_case, Phasen ohne Fallback-Werte) in die interne Form bringen.
const fromApi = (data) => {
  const objects = {};
  Object.entries(data.objects || {}).forEach(([type, entry]) => {
    objects[type] = {
      phases: (entry.phases || []).map((p) => ({
        key: p.key,
        label: p.label,
        next: p.next || undefined,
        checklist: p.checklist || [],
        staleHintDays: p.stale_hint_days ?? null,
        staleWarnDays: p.stale_warn_days ?? null,
      })),
      lost: entry.lost || null,
    };
  });
  return {
    objects,
    lostReasons: data.lost_reasons?.length ? data.lost_reasons : FALLBACK.lostReasons,
    staleHintDays: data.stale_defaults?.hint_days ?? STALE_HINT_DAYS,
    staleWarnDays: data.stale_defaults?.warn_days ?? STALE_WARN_DAYS,
  };
};

export function PipelineConfigProvider({ children }) {
  const [config, setConfig] = useState(FALLBACK);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) return; // Login lädt die Seite neu
    let cancelled = false;
    axiosInstance
      .get('/pipeline-config/')
      .then((res) => { if (!cancelled) setConfig(fromApi(res.data)); })
      .catch(() => { /* Fallback-Konstanten bleiben aktiv */ });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const phasesFor = (type) => config.objects[type]?.phases || FALLBACK.objects[type]?.phases || [];
    const lostFor = (type) => (config.objects[type] ? config.objects[type].lost : FALLBACK.objects[type]?.lost) || null;
    const statusLabelsFor = (type) => {
      const labels = Object.fromEntries(phasesFor(type).map((p) => [p.key, p.label]));
      const lost = lostFor(type);
      if (lost) labels[lost.key] = lost.label;
      return labels;
    };
    return {
      phasesFor,
      lostFor,
      statusLabelsFor,
      lostReasons: config.lostReasons,
      staleHintDays: config.staleHintDays,
      staleWarnDays: config.staleWarnDays,
    };
  }, [config]);

  return (
    <PipelineConfigContext.Provider value={value}>
      {children}
    </PipelineConfigContext.Provider>
  );
}

export function usePipelineConfig() {
  const ctx = useContext(PipelineConfigContext);
  // Ohne Provider (z. B. in Tests): Fallback-Konstanten in Hook-Form.
  if (ctx === FALLBACK) {
    return {
      phasesFor: (type) => FALLBACK.objects[type]?.phases || [],
      lostFor: (type) => FALLBACK.objects[type]?.lost || null,
      statusLabelsFor: (type) => {
        const labels = Object.fromEntries((FALLBACK.objects[type]?.phases || []).map((p) => [p.key, p.label]));
        const lost = FALLBACK.objects[type]?.lost;
        if (lost) labels[lost.key] = lost.label;
        return labels;
      },
      lostReasons: FALLBACK.lostReasons,
      staleHintDays: FALLBACK.staleHintDays,
      staleWarnDays: FALLBACK.staleWarnDays,
    };
  }
  return ctx;
}
